import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import { getUserTimeZone } from '@/common/utils/utils';
import {
  DayOfWeek,
  IntervalUnit,
  type TimeOfDay,
} from '@/proto/logiblocks/logiblocks';
import { DisplayTimeSelection } from '@/pages/Shipments/MapExternalView/types';

/**
 * Converts UTC seconds since midnight to user's configured timezone seconds.
 * @param utcSeconds - Seconds since midnight in UTC (0-86399)
 * @param tzName - User's configured timezone name (e.g., "America/New_York")
 * @returns Seconds since midnight in user's timezone
 */
export function utcSecondsToUserSeconds(
  utcSeconds: number,
  tzName: string
): number {
  const utcHours = Math.floor(utcSeconds / 3600);
  const utcMinutes = Math.floor((utcSeconds % 3600) / 60);
  const m = moment.utc().hours(utcHours).minutes(utcMinutes).seconds(0);
  const userTime = m.tz(tzName);
  return userTime.hours() * 3600 + userTime.minutes() * 60;
}

/**
 * Converts user's configured timezone seconds to UTC seconds since midnight.
 * @param userSeconds - Seconds since midnight in user's timezone
 * @param tzName - User's configured timezone name (e.g., "America/New_York")
 * @returns Seconds since midnight in UTC
 */
export function userSecondsToUtcSeconds(
  userSeconds: number,
  tzName: string
): number {
  const userHours = Math.floor(userSeconds / 3600);
  const userMinutes = Math.floor((userSeconds % 3600) / 60);
  const m = moment.tz({ hours: userHours, minutes: userMinutes }, tzName);
  return m.utc().hours() * 3600 + m.utc().minutes() * 60;
}

/**
 * Converts UTC seconds since midnight to a formatted time string in user's timezone.
 * @param utcSeconds - Seconds since midnight in UTC (0-86399)
 * @returns Formatted time string like "2:30 PM"
 */
export function formatUtcSecondsAsUserTime(utcSeconds: number): string {
  const utcHours = Math.floor(utcSeconds / 3600);
  const utcMinutes = Math.floor((utcSeconds % 3600) / 60);
  const { name: tzName } = getUserTimeZone();
  const m = moment.utc().hours(utcHours).minutes(utcMinutes).seconds(0);
  return m.tz(tzName).format('h:mm A');
}

/** Format timezone offset for display (e.g., "GMT-5" or "GMT+0") */
export function formatTimezoneOffset(offsetMinutes: number): string {
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  return `GMT${sign}${hours}`;
}

/**
 * Converts a duration amount and unit to total seconds.
 * @param amount - The numeric duration value (e.g., 30)
 * @param intervalUnit - The unit (MINUTES or HOURS)
 * @returns Total duration in seconds
 */
export function durationToSeconds(
  amount: number,
  intervalUnit: IntervalUnit
): number {
  return Math.round(amount * (intervalUnit === IntervalUnit.HOURS ? 3600 : 60));
}

/**
 * Converts a number of seconds to the given duration unit.
 * @param seconds
 * @param unit - The unit (MINUTES or HOURS)
 * @returns Total duration in the given unit
 */
export function secondsToDuration(seconds: number, unit: IntervalUnit): number {
  return unit === IntervalUnit.HOURS ? seconds / 3600 : seconds / 60;
}

const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.SUNDAY]: 'Sunday',
  [DayOfWeek.MONDAY]: 'Monday',
  [DayOfWeek.TUESDAY]: 'Tuesday',
  [DayOfWeek.WEDNESDAY]: 'Wednesday',
  [DayOfWeek.THURSDAY]: 'Thursday',
  [DayOfWeek.FRIDAY]: 'Friday',
  [DayOfWeek.SATURDAY]: 'Saturday',
  [DayOfWeek.UNRECOGNIZED]: 'Unknown',
};

const DAY_OF_WEEK_SHORT_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.SUNDAY]: 'Sun',
  [DayOfWeek.MONDAY]: 'Mon',
  [DayOfWeek.TUESDAY]: 'Tue',
  [DayOfWeek.WEDNESDAY]: 'Wed',
  [DayOfWeek.THURSDAY]: 'Thu',
  [DayOfWeek.FRIDAY]: 'Fri',
  [DayOfWeek.SATURDAY]: 'Sat',
  [DayOfWeek.UNRECOGNIZED]: '???',
};

const DAY_OF_WEEK_NARROW_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.SUNDAY]: 'Su',
  [DayOfWeek.MONDAY]: 'Mo',
  [DayOfWeek.TUESDAY]: 'Tu',
  [DayOfWeek.WEDNESDAY]: 'We',
  [DayOfWeek.THURSDAY]: 'Th',
  [DayOfWeek.FRIDAY]: 'Fr',
  [DayOfWeek.SATURDAY]: 'Sa',
  [DayOfWeek.UNRECOGNIZED]: '??',
};

interface HourMinuteResult {
  hour: number;
  minute: number;
  /** -1 = previous day, 0 = same day, +1 = next day */
  dayOffset: number;
}

/**
 * Converts UTC hour/minute to the user's configured timezone.
 * Returns the local hour, minute, and a dayOffset indicating if the day shifted.
 */
export function utcHourMinuteToUser(
  utcHour: number,
  utcMinute: number,
  tzName: string
): HourMinuteResult {
  // Anchor to the 15th to avoid month-boundary issues with .date() diff
  const m = moment.utc().date(15).hours(utcHour).minutes(utcMinute).seconds(0);
  const utcDay = m.date();
  const userTime = m.tz(tzName);
  const userDay = userTime.date();
  return {
    hour: userTime.hours(),
    minute: userTime.minutes(),
    dayOffset: userDay - utcDay,
  };
}

/**
 * Converts user-local hour/minute to UTC.
 * Returns the UTC hour, minute, and a dayOffset indicating if the day shifted.
 */
export function userHourMinuteToUtc(
  userHour: number,
  userMinute: number,
  tzName: string
): HourMinuteResult {
  // Anchor to the 15th to avoid month-boundary issues with .date() diff
  const m = moment.tz(
    { date: 15, hours: userHour, minutes: userMinute, seconds: 0 },
    tzName
  );
  const localDay = m.date();
  const utc = m.utc();
  const utcDay = utc.date();
  return {
    hour: utc.hours(),
    minute: utc.minutes(),
    dayOffset: utcDay - localDay,
  };
}

/** Shifts a single DayOfWeek value by dayOffset, wrapping Sun↔Sat. */
export function shiftDayOfWeek(day: DayOfWeek, dayOffset: number): DayOfWeek {
  if (day === DayOfWeek.UNRECOGNIZED) {
    return DayOfWeek.UNRECOGNIZED;
  }
  return (((day + dayOffset) % 7) + 7) % 7;
}

/** Convert seconds since midnight to a dayjs time-of-day for antd TimePicker. */
export function secondsToDayjs(seconds: number): Dayjs {
  return dayjs().startOf('day').add(seconds, 'second');
}

/** Convert a dayjs time-of-day to seconds since midnight. */
export function dayjsToSeconds(time: Dayjs): number {
  return time.hour() * 3600 + time.minute() * 60 + time.second();
}

/** Format already-local seconds since midnight as a 12-hour time string (e.g. 34200 → "9:30 AM"). */
export function formatLocalSecondsAsTime(seconds: number): string {
  return secondsToDayjs(seconds).format('h:mm A');
}

/** Formats hour and minute as zero-padded "HH:MM" (e.g. 9, 5 → "09:05"). */
export function formatHourMinute(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Format a schedule's local time-of-day as a 12-hour string (e.g. {9, 0} → "9:00 AM"). */
export function formatTimeOfDay(time: TimeOfDay): string {
  return dayjs().hour(time.hour).minute(time.minute).format('h:mm A');
}

/**
 * follows the user's date_format preference; defaults to
 * MM/DD/YYYY-style ordering when omitted.
 */
export function formatDateTime(
  iso: string,
  dateFormat?: string | null
): string {
  if (dateFormat === 'YYYY/MM/DD') {
    return dayjs(iso).format('YYYY, MMM D, h:mm A');
  }
  const monthDay = dateFormat === 'DD/MM/YYYY' ? 'D MMM' : 'MMM D';
  return dayjs(iso).format(`${monthDay}, YYYY, h:mm A`);
}

/** Format a day-of-month number as an English ordinal (e.g. 1 → "1st", 22 → "22nd"). */
export function formatOrdinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) {
    return `${n}th`;
  }
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** Full day name for a DayOfWeek enum value (e.g. "Monday"). */
export function getDayOfWeekLabel(day: DayOfWeek): string {
  return DAY_OF_WEEK_LABELS[day] ?? 'Unknown';
}

/** Abbreviated day name for a DayOfWeek enum value (e.g. "Mon"). */
export function getDayOfWeekShortLabel(day: DayOfWeek): string {
  return DAY_OF_WEEK_SHORT_LABELS[day] ?? '???';
}

/** Two-letter day name for a DayOfWeek enum value (e.g. "Mo"). */
export function getDayOfWeekNarrowLabel(day: DayOfWeek): string {
  return DAY_OF_WEEK_NARROW_LABELS[day] ?? '??';
}

/** Resolve a DisplayTimeSelection to a concrete IANA timezone string.
 *  "my_local_time" uses the account-configured timezone (JWT → localStorage → browser fallback).
 *  "my_browser_time" uses the raw OS/browser locale. */
export function resolveIana(sel: DisplayTimeSelection): string {
  if (sel.type === 'timezone') return sel.value;
  if (sel.value === 'my_browser_time')
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  return getUserTimeZone().name;
}
